import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import axios from 'axios';
import { labelSx } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

function hoy() { return new Date().toISOString().split('T')[0]; }

// El link de confirmación vence a la hora del turno — no tiene sentido mandar una
// solicitud (o mostrar el botón) para un turno cuyo horario ya pasó, nacería vencida.
function turnoYaPaso(t) {
  const fechaStr = String(t.fecha).split('T')[0];
  const hora = String(t.hora_inicio).substring(0, 5);
  return new Date(`${fechaStr}T${hora}:00`) < new Date();
}

const ESTADO_CFG = {
  confirmada: { icono: '🟢', label: 'Confirmó', color: '#81c784' },
  rechazada:  { icono: '🔴', label: 'Indicó que no asistirá', color: '#ef5350' },
  pendiente:  { icono: '🟡', label: 'Pendiente', color: '#ffb74d' },
};

function LF({ label, children }) {
  return <Box><Typography sx={labelSx}>{label}</Typography>{children}</Box>;
}

function CardContador({ label, valor, color }) {
  return (
    <Box sx={{ flex: 1, minWidth: 120, border: `1px solid ${color}55`, bgcolor: `${color}14`, borderRadius: '8px', p: '14px 16px' }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 1, mb: '6px' }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{valor}</Typography>
    </Box>
  );
}

export default function Confirmaciones() {
  const [fecha, setFecha] = useState(hoy());
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/turnos?fecha=${fecha}`);
      setTurnos((r.data.data || []).filter(t => !['cancelado', 'atendido'].includes(t.estado)));
    } catch { setTurnos([]); }
    setLoading(false);
  }, [fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  async function enviarSolicitud(turnoId) {
    setEnviando(turnoId);
    try {
      await axios.post(`${API}/api/turnos/${turnoId}/enviar-confirmacion-asistencia`);
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo enviar la solicitud.');
    }
    setEnviando(null);
  }

  // El paciente confirmó por teléfono o se acercó en persona — no pasó por el email.
  async function marcarManual(turnoId, estado) {
    const pregunta = estado === 'confirmada'
      ? '¿Marcar como confirmado? (el paciente avisó por otro medio)'
      : '¿Marcar que no va a asistir? (el paciente avisó por otro medio)';
    if (!window.confirm(pregunta)) return;
    setEnviando(turnoId);
    try {
      await axios.post(`${API}/api/turnos/${turnoId}/confirmacion-asistencia/manual`, { estado });
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo registrar la confirmación.');
    }
    setEnviando(null);
  }

  // Los turnos especiales no participan del proceso de confirmación de asistencia -- no deben
  // contarse en ninguno de estos contadores. Pendiente de decidir todavía: si la FILA de un turno
  // especial se sigue mostrando en la lista de abajo (con algo tipo "No aplica") o se excluye
  // directamente -- eso no se tocó acá, solo se corrigieron los números.
  const confirmados = turnos.filter(t => t.confirmacion_estado === 'confirmada' && !t.especial).length;
  const pendientes  = turnos.filter(t => t.confirmacion_estado === 'pendiente' && !t.especial).length;
  const noAsisten   = turnos.filter(t => t.confirmacion_estado === 'rechazada' && !t.especial).length;
  const sinEnviar   = turnos.filter(t => !t.confirmacion_estado && !t.especial).length;

  return (
    <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box>
        <Typography variant="h6" sx={{ color: 'var(--c-texto)', fontWeight: 700, fontSize: 18 }}>
          Confirmaciones
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#546e7a', mt: '2px' }}>
          Quién confirmó, quién no respondió y quién avisó que no asistirá
        </Typography>
      </Box>

      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end',
        p: '16px', bgcolor: 'var(--c-superficie)', borderRadius: '8px',
        border: '1px solid rgba(144,202,249,0.1)',
      }}>
        <LF label="Fecha objetivo">
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ height: 40, padding: '0 10px', borderRadius: 6, border: '1px solid rgba(144,202,249,0.2)', background: 'var(--c-fondo)', color: 'var(--c-texto)', fontSize: 13, colorScheme: 'dark', outline: 'none', cursor: 'pointer' }} />
        </LF>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}

      {!loading && (
        <>
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <CardContador label="Turnos" valor={turnos.length} color="#90caf9" />
            <CardContador label="Confirmados" valor={confirmados} color="#81c784" />
            <CardContador label="Pendientes" valor={pendientes} color="#ffb74d" />
            <CardContador label="No asistirán" valor={noAsisten} color="#ef5350" />
          </Box>

          <Box>
            {turnos.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: '#455a64', fontSize: 13 }}>
                No hay turnos para esta fecha.
              </Box>
            ) : (
              turnos
                .slice()
                .sort((a, b) => String(a.hora_inicio).localeCompare(String(b.hora_inicio)))
                .map(t => {
                  const cfg = t.confirmacion_estado ? ESTADO_CFG[t.confirmacion_estado] : null;
                  return (
                    <Box key={t.id} sx={{
                      display: 'flex', alignItems: 'center', gap: '16px', p: '12px 16px', mb: '8px',
                      bgcolor: 'var(--c-superficie)', borderRadius: '8px', border: '1px solid rgba(144,202,249,0.08)',
                    }}>
                      <Typography sx={{ fontSize: 13, color: '#90a4ae', minWidth: 50 }}>
                        {String(t.hora_inicio).substring(0, 5)}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 160 }}>
                        <Typography sx={{ fontSize: 14, color: 'var(--c-texto)', fontWeight: 500 }}>
                          {t.paciente_nombre}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#607d8b' }}>
                          {t.medico_nombre || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        {cfg ? (
                          <Typography sx={{ fontSize: 13, color: cfg.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {cfg.icono} {cfg.label}{t.confirmacion_canal === 'manual' ? ' (por otro medio)' : ''}
                          </Typography>
                        ) : turnoYaPaso(t) ? (
                          <Typography sx={{ fontSize: 12, color: '#546e7a', whiteSpace: 'nowrap' }}>
                            Turno ya pasó
                          </Typography>
                        ) : t.especial ? (
                          // Un turno especial no participa del proceso de confirmación de asistencia
                          // (el backend también lo rechaza) -- se muestra por qué en vez de dejar un
                          // botón que tira error al tocarlo. Sigue pendiente decidir si la fila entera
                          // se sigue mostrando así o se excluye -- esto solo evita el botón roto.
                          <Typography sx={{ fontSize: 12, color: '#546e7a', whiteSpace: 'nowrap' }}>
                            No aplica — turno especial
                          </Typography>
                        ) : (
                          <Button size="small" variant="outlined" disabled={enviando === t.id || !t.paciente_email}
                            onClick={() => enviarSolicitud(t.id)}
                            title={!t.paciente_email ? 'El paciente no tiene email registrado' : ''}
                            sx={{ color: 'var(--c-acento)', borderColor: 'rgba(144,202,249,0.3)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                            {enviando === t.id ? 'Enviando...' : 'Enviar solicitud'}
                          </Button>
                        )}
                        {t.confirmacion_estado !== 'confirmada' && (
                          <Box sx={{ display: 'flex', gap: '10px' }}>
                            <Typography component="button" onClick={() => marcarManual(t.id, 'confirmada')} disabled={enviando === t.id}
                              sx={{ fontSize: 10.5, color: '#66bb6a', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                              Marcar confirmado
                            </Typography>
                            {t.confirmacion_estado !== 'rechazada' && (
                              <Typography component="button" onClick={() => marcarManual(t.id, 'rechazada')} disabled={enviando === t.id}
                                sx={{ fontSize: 10.5, color: '#78909c', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                                No asiste
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })
            )}
          </Box>

          {sinEnviar > 0 && (
            <Typography sx={{ fontSize: 11.5, color: '#546e7a' }}>
              {sinEnviar} turno{sinEnviar !== 1 ? 's' : ''} todavía sin solicitud de confirmación enviada.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
